import type { Lesson } from "./types";

/**
 * Booking-window helpers for the student-facing surfaces (issues #36/#37).
 *
 * Every lesson carries a resolved `booking_cutoff_minutes` (the API
 * applies the global default when the teacher hasn't set an override),
 * so clients can compute the close instant purely from the lesson.
 * `booking_open_days` is `null` when there is no early restriction.
 *
 * Boundary semantics match the backend: a lesson is *open* while
 * `open_at <= now <= cutoff` (the exact boundary instants still allow
 * booking) and *blocked* outside that band — `notOpen` before the open
 * bound, `closed` past the cutoff.
 */

/** The exact instant reservations close: `starts_at - cutoff`. */
export function bookingCutoffAt(lesson: Lesson): Date {
  return new Date(new Date(lesson.starts_at).getTime() - lesson.booking_cutoff_minutes * 60_000);
}

/**
 * The exact instant reservations open: `starts_at - open_days`, or
 * `null` when the lesson has no early window (open immediately).
 */
export function bookingOpenAt(lesson: Lesson): Date | null {
  if (lesson.booking_open_days === null || lesson.booking_open_days === undefined) {
    return null;
  }
  return new Date(new Date(lesson.starts_at).getTime() - lesson.booking_open_days * 86_400_000);
}

/** True once the lesson's booking window has closed (`now > cutoff`). */
export function isBookingsClosed(lesson: Lesson, now: Date): boolean {
  return bookingCutoffAt(lesson).getTime() < now.getTime();
}

/**
 * True when the lesson's booking window has not opened yet
 * (`now < open_at`; only when an early window is configured).
 */
export function isBookingsNotOpen(lesson: Lesson, now: Date): boolean {
  const openAt = bookingOpenAt(lesson);
  return openAt !== null && now.getTime() < openAt.getTime();
}
